/**
 * MotorOnderdelenView - Toont alle onderdelen voor een specifieke motor
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiFetch from '../../services/api';
import ErrorDialog from '../../components/ErrorDialog';
import { useAuth } from '../../context/AuthContext';
import '../voorraad/Voorraad.css';

interface VoorraadItem {
  id: number;
  groep_id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  leverancier: string | null;
  artikelnummer: string | null;
  omschrijving: string | null;
  levensduur_maanden: number | null;
  aantal_voorraad: number;
  min_voorraad: number | null;
  inkoopprijs: number | null;
  verkoopprijs: number | null;
  // Motor specifieke velden
  vermogen_pk?: number | null;
  vermogen_kw?: number | null;
  cilinders?: number | null;
  brandstof_type?: string | null;
  koelsysteem?: string | null;
  motor_model_id?: number | null;
  status: string;
}

interface MotorModel {
  id: number;
  fabrikant: string;
  model: string;
  type: string;
  vermogen_pk?: number;
  vermogen_kw?: number;
}

const MotorOnderdelenView: React.FC = () => {
  const { motorId } = useParams<{ motorId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [motorModel, setMotorModel] = useState<MotorModel | null>(null);
  const [onderdelen, setOnderdelen] = useState<VoorraadItem[]>([]);
  const [filteredOnderdelen, setFilteredOnderdelen] = useState<VoorraadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [fabrikantFilter, setFabrikantFilter] = useState('');

  // Filter options
  const [onderdeelTypes, setOnderdeelTypes] = useState<string[]>([]);
  const [fabrikanten, setFabrikanten] = useState<string[]>([]);

  useEffect(() => {
    if (motorId) {
      Promise.all([
        loadMotorModel(),
        loadMotorOnderdelen()
      ]);
    }
  }, [motorId]);

  useEffect(() => {
    // Filter onderdelen
    let filtered = onderdelen;

    if (typeFilter) {
      filtered = filtered.filter(item => item.onderdeel_type === typeFilter);
    }

    if (fabrikantFilter) {
      filtered = filtered.filter(item => item.fabrikant === fabrikantFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.onderdeel_type.toLowerCase().includes(term) ||
        (item.fabrikant && item.fabrikant.toLowerCase().includes(term)) ||
        (item.model && item.model.toLowerCase().includes(term)) ||
        (item.artikelnummer && item.artikelnummer.toLowerCase().includes(term)) ||
        (item.omschrijving && item.omschrijving.toLowerCase().includes(term))
      );
    }

    setFilteredOnderdelen(filtered);
  }, [onderdelen, typeFilter, fabrikantFilter, searchTerm]);

  const loadMotorModel = async () => {
    try {
      // Haal motor model op via voorraad items endpoint
      const voorraadData = await apiFetch(`/api/v1/voorraad/items/${motorId}`);
      setMotorModel({
        id: voorraadData.id,
        fabrikant: voorraadData.fabrikant || '',
        model: voorraadData.model || '',
        type: voorraadData.onderdeel_type,
        vermogen_pk: voorraadData.vermogen_pk,
        vermogen_kw: voorraadData.vermogen_kw
      });
    } catch (err: any) {
      console.error('Error loading motor model:', err);
      setError(`Motor model niet gevonden (ID: ${motorId})`);
    }
  };

  const loadMotorOnderdelen = async () => {
    try {
      setLoading(true);
      
      // Gebruik de nieuwe motor-onderdelen endpoint
      const data: VoorraadItem[] = await apiFetch(`/api/v1/voorraad/motor-onderdelen/${motorId}`);
      
      setOnderdelen(data);
      
      // Extract unique values voor filters
      const uniqueTypes = [...new Set(data.map(o => o.onderdeel_type).filter(Boolean))].sort();
      const uniqueFabrikanten = [...new Set(data.map(o => o.fabrikant).filter((f): f is string => f !== null))].sort();
      
      setOnderdeelTypes(uniqueTypes);
      setFabrikanten(uniqueFabrikanten);
      
    } catch (err: any) {
      console.error('Error loading motor onderdelen:', err);
      setError(err.message || 'Er is een fout opgetreden bij het laden van motor onderdelen');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Motor onderdelen laden...</p>
        </div>
      </div>
    );
  }

  if (!motorModel) {
    return (
      <div className="container">
        <div className="error-state">
          <h2>Motor niet gevonden</h2>
          <p>De motor met ID {motorId} kon niet worden gevonden.</p>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">
            Terug
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header met motor informatie */}
      <div className="page-header">
        <div>
          <nav className="breadcrumb">
            <Link to="/voorraad?categorie=50000">Motoren en Onderdelen</Link>
            <span> / </span>
            <span>Onderdelen voor {motorModel.fabrikant} {motorModel.model}</span>
          </nav>
          <h1>Onderdelen voor {motorModel.fabrikant} {motorModel.model}</h1>
          {motorModel.type && <p className="page-subtitle">{motorModel.type}</p>}
          {(motorModel.vermogen_pk || motorModel.vermogen_kw) && (
            <p className="page-subtitle">
              {motorModel.vermogen_pk && `${motorModel.vermogen_pk} pk`}
              {motorModel.vermogen_kw && ` (${motorModel.vermogen_kw} kW)`}
            </p>
          )}
        </div>
        <div className="page-actions">
          {hasPermission('motor_onderdelen_aanmaken') && (
            <button className="btn btn-primary">
              + Nieuw Onderdeel
            </button>
          )}
        </div>
      </div>

      {/* Search en Filters */}
      <div className="search-filters-section" style={{ marginBottom: '1rem' }}>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Zoek op type, fabrikant, artikel nummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters">
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle onderdeel types</option>
            {onderdeelTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select 
            value={fabrikantFilter} 
            onChange={(e) => setFabrikantFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle fabrikanten</option>
            {fabrikanten.map(fabrikant => (
              <option key={fabrikant} value={fabrikant}>{fabrikant}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-info">
        <span className="results-count">{filteredOnderdelen.length} onderdelen gevonden</span>
      </div>

      {/* Onderdelen Lijst */}
      {filteredOnderdelen.length > 0 ? (
        <div className="voorraad-table-container">
          <div className="table-header">
            <div style={{ width: '200px', flexShrink: 0 }}>Type</div>
            <div style={{ width: '150px', flexShrink: 0 }}>Fabrikant</div>
            <div style={{ width: '150px', flexShrink: 0 }}>Model</div>
            <div style={{ width: '120px', flexShrink: 0 }}>Artikel nr.</div>
            <div style={{ width: '80px', flexShrink: 0, textAlign: 'center' }}>Voorraad</div>
            <div style={{ width: '100px', flexShrink: 0, textAlign: 'center' }}>Status</div>
            <div style={{ width: '200px', flexShrink: 0 }}>Acties</div>
          </div>
          
          <div className="table-body">
            {filteredOnderdelen.map(item => (
              <div key={item.id} className="table-row">
                <div style={{ width: '200px', flexShrink: 0, fontWeight: '500' }}>
                  {item.onderdeel_type}
                </div>
                <div style={{ width: '150px', flexShrink: 0 }}>
                  {item.fabrikant || '-'}
                </div>
                <div style={{ width: '150px', flexShrink: 0 }}>
                  {item.model || '-'}
                </div>
                <div style={{ width: '120px', flexShrink: 0 }}>
                  {item.artikelnummer || '-'}
                </div>
                <div style={{ width: '80px', flexShrink: 0, textAlign: 'center', fontWeight: '600' }}>
                  {item.aantal_voorraad}
                </div>
                <div style={{ width: '100px', flexShrink: 0, textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    minWidth: '50px',
                    textAlign: 'center',
                    background: item.status === 'kritiek' ? '#fee2e2' : item.status === 'waarschuwing' ? '#fef3c7' : '#d1fae5',
                    color: item.status === 'kritiek' ? '#991b1b' : item.status === 'waarschuwing' ? '#92400e' : '#065f46'
                  }}>
                    {item.status === 'kritiek' ? 'Kritiek' : item.status === 'waarschuwing' ? 'Laag' : 'OK'}
                  </span>
                </div>
                <div style={{ width: '200px', flexShrink: 0 }}>
                  {hasPermission('producten_bewerken') && (
                    <button
                      className="btn-small btn-secondary"
                      onClick={() => navigate(`/onderdelen/${item.id}`)}
                      style={{ whiteSpace: 'nowrap', marginRight: '0.5rem' }}
                    >
                      Bewerken
                    </button>
                  )}
                  <button
                    className="btn-small btn-success"
                    onClick={() => navigate(`/onderdelen/${item.id}/documenten`)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Documenten
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">🔧</div>
          <h3>Geen onderdelen gevonden</h3>
          <p>
            {searchTerm || typeFilter || fabrikantFilter
              ? `Geen onderdelen gevonden voor ${motorModel.fabrikant} ${motorModel.model} met de huidige filters.`
              : `Er zijn nog geen onderdelen geregistreerd voor ${motorModel.fabrikant} ${motorModel.model}.`
            }
          </p>
          {(searchTerm || typeFilter || fabrikantFilter) && (
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('');
                setFabrikantFilter('');
              }}
            >
              Filters wissen
            </button>
          )}
        </div>
      )}

      <ErrorDialog isOpen={!!error} message={error || ''} onClose={() => setError(null)} />
    </div>
  );
};

export default MotorOnderdelenView;